import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { blogAPI } from '../services/api';
import Header from './Header';
import Footer from './Footer';
import RelatedPosts from './RelatedPosts';
import NotFoundPage from './NotFoundPage';
import { Calendar, ChevronLeft, User, BookOpen, AlertTriangle, RefreshCcw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const BlogPostPage = () => {
  const { slug } = useParams();
  const { currentLanguage, t } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const postData = await blogAPI.getPostBySlug(slug);
        setPost(postData);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Post not found');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const getBlogTitle = () => {
    if (!post) return '';
    return post.title[currentLanguage] || post.title.en || post.title;
  };

  const getBlogContent = () => {
    if (!post?.content) return '';
    return post.content[currentLanguage] || post.content.en || '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const localeMap = {
      en: 'en-US', es: 'es-ES', ru: 'ru-RU', de: 'de-DE', fr: 'fr-FR',
      it: 'it-IT', pl: 'pl-PL', nl: 'nl-NL', pt: 'pt-BR',
    };
    return date.toLocaleDateString(localeMap[currentLanguage] || 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Localised research disclaimer for all 9 supported languages.
  const disclaimerText = {
    en: 'Important Notice: This article is for informational and educational purposes only. All products mentioned are exclusively for scientific research and are not intended for human consumption or therapeutic use.',
    es: 'Aviso Importante: Este artículo es únicamente con fines informativos y educativos. Todos los productos mencionados son exclusivamente para investigación científica y no están destinados al consumo humano ni para uso terapéutico.',
    ru: 'Важное Примечание: Эта статья предназначена только для информационных и образовательных целей. Все упомянутые продукты предназначены исключительно для научных исследований и не предназначены для употребления человеком или терапевтического использования.',
    de: 'Wichtiger Hinweis: Dieser Artikel dient ausschließlich Informations- und Bildungszwecken. Alle genannten Produkte sind ausschließlich für die wissenschaftliche Forschung bestimmt und nicht für den menschlichen Verzehr oder therapeutische Anwendung vorgesehen.',
    fr: 'Avis Important : Cet article est uniquement à des fins d\'information et d\'éducation. Tous les produits mentionnés sont exclusivement destinés à la recherche scientifique et ne sont pas destinés à la consommation humaine ni à un usage thérapeutique.',
    it: 'Avviso Importante: Questo articolo è solo a scopo informativo ed educativo. Tutti i prodotti menzionati sono esclusivamente per la ricerca scientifica e non sono destinati al consumo umano né a uso terapeutico.',
    pl: 'Ważna Informacja: Ten artykuł ma wyłącznie charakter informacyjny i edukacyjny. Wszystkie wymienione produkty są przeznaczone wyłącznie do badań naukowych i nie są przeznaczone do spożycia przez ludzi ani do celów terapeutycznych.',
    nl: 'Belangrijke Mededeling: Dit artikel is uitsluitend voor informatieve en educatieve doeleinden. Alle genoemde producten zijn uitsluitend voor wetenschappelijk onderzoek en niet bedoeld voor menselijke consumptie of therapeutisch gebruik.',
    pt: 'Aviso Importante: Este artigo é apenas para fins informativos e educacionais. Todos os produtos mencionados são exclusivamente para pesquisa científica e não se destinam ao consumo humano nem a uso terapêutico.',
  };

  const backText = {
    en: 'Back to all articles',
    es: 'Volver a todos los artículos',
    ru: 'Вернуться ко всем статьям',
    de: 'Zurück zu allen Artikeln',
    fr: 'Retour à tous les articles',
    it: 'Torna a tutti gli articoli',
    pl: 'Powrót do wszystkich artykułów',
    nl: 'Terug naar alle artikelen',
    pt: 'Voltar para todos os artigos',
  };

  // Inject `BlogPosting` JSON-LD on the post page so Google News, Discover,
  // and other crawlers can classify and rank the article. Cleanup on unmount
  // / locale change so we never leave stale schema in the DOM.
  useEffect(() => {
    if (!post) return undefined;
    const id = 'blog-posting-jsonld';
    document.querySelectorAll(`script#${id}`).forEach((n) => n.remove());

    const headline = (post.title?.[currentLanguage] || post.title?.en || '').slice(0, 110);
    const description = (post.excerpt?.[currentLanguage] || post.excerpt?.en || '').slice(0, 300);
    const content = post.content?.[currentLanguage] || post.content?.en || '';
    const wordCount = content ? content.split(/\s+/).filter(Boolean).length : 0;
    const canonical = `https://penlabpeptide.com/${currentLanguage}/blog/${post.slug || ''}`;
    const image = post.image_url || 'https://penlabpeptide.com/logo512.png';
    const pub = post.published_date || post.created_at || new Date().toISOString();
    const mod = post.updated_at || pub;

    // Map our lang prefix codes to BCP-47 form so the JSON-LD `inLanguage`
    // matches the backend `_inject_head` `<html lang>` + `og:locale`. Mixed
    // signals soften Google's per-locale ranking confidence; aligning them
    // pushes per-market visibility up another notch.
    const BCP47 = {
      en: 'en', es: 'es', ru: 'ru', de: 'de', fr: 'fr',
      it: 'it', pl: 'pl', nl: 'nl', pt: 'pt-BR', au: 'en-AU',
    };
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline,
      description,
      inLanguage: BCP47[currentLanguage] || currentLanguage,
      datePublished: pub,
      dateModified: mod,
      image: image ? [image] : [],
      author: {
        '@type': 'Organization',
        name: post.author || 'Penlab Peptide Research Team',
        url: 'https://penlabpeptide.com',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Penlab Peptide',
        logo: {
          '@type': 'ImageObject',
          url: 'https://penlabpeptide.com/logo512.png',
          width: 512,
          height: 512,
        },
        url: 'https://penlabpeptide.com',
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
      articleSection: 'Research Peptides',
      wordCount,
      isAccessibleForFree: true,
      ...(post.target_keyword ? { keywords: post.target_keyword } : {}),
    };

    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => script.remove();
  }, [post, currentLanguage]);

  if (loading) {
    return (
      <div className="min-h-screen bg-capsule-bg">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="animate-pulse">
            <div className="h-8 bg-white/50 rounded-2xl w-3/4 mb-4"></div>
            <div className="h-4 bg-white/50 rounded-2xl w-1/4 mb-8"></div>
            <div className="h-64 bg-white/50 rounded-3xl mb-8"></div>
            <div className="space-y-4">
              <div className="h-4 bg-white/50 rounded-2xl"></div>
              <div className="h-4 bg-white/50 rounded-2xl"></div>
              <div className="h-4 bg-white/50 rounded-2xl w-5/6"></div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    // Render the noindex-emitting 404 component instead of a soft-404
    // ("Article not found" page that returns HTTP 200). Stops Google from
    // keeping deleted/renamed blog slugs in its index.
    return <NotFoundPage />;
  }

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />

      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        {/* DNA Decoration */}
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="blogPostGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#blogPostGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#blogPostGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        {/* Floating spheres */}
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-20 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 py-12 md:py-16">
          {/* Breadcrumb */}
          <div className="flex items-center text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-capsule-aqua transition-colors">{t('nav_home')}</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-capsule-aqua transition-colors">{t('nav_blog')}</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-300 truncate max-w-xs">{getBlogTitle()}</span>
          </div>
          
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
            <BookOpen className="w-4 h-4 text-capsule-aqua" />
            <span className="text-sm font-medium text-capsule-aqua">{t('blog_label')}</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            {getBlogTitle()}
          </h1>
          
          <div className="flex items-center gap-6 text-gray-300 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-capsule-aqua" />
              <span>{formatDate(post.published_date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-capsule-aqua" />
              <span>PenLab Peptide</span>
            </div>
            {/* Last-updated badge, research-content readers (and Google
                News' freshness algorithm) explicitly weight when an article
                was last revised. Only render when `updated_at` is materially
                later (>1 day) than `published_date` so we don't show a
                redundant badge on never-revised posts. Falls back gracefully
                to `translations_completed_at` for posts where the translation
                refresh is the most recent revision signal. */}
            {(() => {
              const pub = post.published_date ? new Date(post.published_date) : null;
              const upd = post.updated_at ? new Date(post.updated_at) : null;
              const tr = post.translations_completed_at
                ? new Date(post.translations_completed_at)
                : null;
              const latest = [upd, tr].filter(Boolean)
                .sort((a, b) => b - a)[0] || null;
              if (!pub || !latest) return null;
              const dayDiff = (latest - pub) / 86400000;
              if (dayDiff < 1) return null;
              return (
                <div
                  className="flex items-center gap-2 px-3 py-1 rounded-full bg-capsule-aqua/10 border border-capsule-aqua/30"
                  data-testid="blog-last-updated-badge"
                  title={`${t('blog_last_updated_tooltip') || 'Last revised'}: ${latest.toISOString().slice(0,10)}`}
                >
                  <RefreshCcw className="w-4 h-4 text-capsule-aqua" />
                  <span className="text-sm text-capsule-aqua">
                    {(t('blog_last_updated') || 'Updated')} {formatDate(latest.toISOString())}
                  </span>
                </div>
              );
            })()}
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 38 1344 33 1392 30.8L1440 29V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#E7EAED"/>
          </svg>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        {/* Featured Image */}
        {post.image_url && (
          <div className="mb-10 glass-card rounded-3xl p-4 overflow-hidden">
            <img
              src={post.image_url}
              alt={getBlogTitle()}
              className="w-full h-auto max-h-[400px] object-cover rounded-2xl"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Content */}
        <div className="glass-card rounded-3xl p-8 md:p-10">
          <div className="prose prose-lg max-w-none text-gray-700">
            <ReactMarkdown
              // Enable GFM tables + strikethrough + task-lists so pricing
              // and comparison tables in blog articles render as real
              // <table> elements instead of raw pipe-text (iter-65 fix).
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-gray-900 mt-8 mb-4" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 pb-2 border-b border-gray-200" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3" {...props} />,
                h4: ({node, ...props}) => <h4 className="text-lg font-semibold text-gray-700 mt-4 mb-2" {...props} />,
                p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 mb-4 ml-4" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-2 mb-4 ml-4" {...props} />,
                li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
                strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                em: ({node, ...props}) => <em className="italic" {...props} />,
                blockquote: ({node, ...props}) => (
                  <blockquote className="border-l-4 border-capsule-teal pl-4 py-2 my-4 bg-capsule-teal/5 rounded-r-lg italic text-gray-600" {...props} />
                ),
                code: ({node, inline, ...props}) =>
                  inline ? (
                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono" {...props} />
                  ) : (
                    <code className="block bg-gray-100 p-4 rounded-lg text-sm font-mono overflow-x-auto" {...props} />
                  ),
                a: ({node, ...props}) => <a className="text-capsule-teal hover:text-capsule-teal-dark underline" {...props} />,
                // Table styling via GFM plugin.
                table: ({node, ...props}) => <div className="overflow-x-auto my-6"><table className="w-full text-sm border-collapse" {...props} /></div>,
                thead: ({node, ...props}) => <thead className="bg-gradient-to-r from-capsule-teal/10 to-capsule-aqua/10" {...props} />,
                th: ({node, ...props}) => <th className="text-left px-4 py-3 font-semibold text-gray-800 border-b border-gray-200" {...props} />,
                td: ({node, ...props}) => <td className="px-4 py-3 text-gray-700 border-b border-gray-100" {...props} />,
                tr: ({node, ...props}) => <tr className="hover:bg-white/40 transition-colors" {...props} />,
              }}
            >
              {getBlogContent()}
            </ReactMarkdown>
          </div>
        </div>

        {/* Related Posts + Products, internal-linking SEO block.
            Added iter-44 to address Semrush "3,322 orphaned sitemap pages"
            and to push PageRank into long-tail product/blog URLs that
            previously had zero in-domain inbound links. Component renders
            nothing if the API has no candidate posts/products. */}
        <RelatedPosts currentPost={post} />

        {/* Research Disclaimer */}
        <div className="mt-10 p-6 bg-gradient-to-r from-capsule-teal/10 via-capsule-aqua/5 to-capsule-teal/10 border border-capsule-teal/20 rounded-2xl">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-gradient-to-br from-capsule-coral to-capsule-salmon rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              {disclaimerText[currentLanguage] || disclaimerText.en}
            </p>
          </div>
        </div>

        {/* Back to Blog */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/blog"
            className="inline-flex items-center text-capsule-teal hover:text-capsule-teal-dark font-medium transition-colors"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            {backText[currentLanguage] || backText.en}
          </Link>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
